import { useEffect, useState } from 'react';
import { Heart } from 'lucide-react';

// Rotating gratitude notes from Thrive Management. Kept short, warm and
// family-focused — never cheesy. One message rotates every ~30 seconds
// so a therapist glancing at the sidebar sees fresh appreciation across
// the day, but not so fast that it feels distracting.
const MESSAGES = [
  'Thrive Management appreciates your dedication to our families.',
  'Every note you finish, every family you welcome — it matters.',
  'You are the reason a child said their first word today.',
  'Thrive is stronger because of the quiet, careful work you do.',
  'Thank you for choosing patience on the hard days.',
  'Behind every milestone is a therapist who showed up. That’s you.',
  'Our families feel your care long after the session ends.',
  'The little things you do here ripple through nine communities.',
  'Thrive Management is grateful for the heart you bring to work.',
  'You are building futures — one visit, one word, one family at a time.',
  'Thank you for making Thrive feel like home to our families.',
  'The work is worth it because you make it so. Thank you.',
];

// Deterministic seed for the initial pick so a page refresh doesn't
// always show the same message — but sessions during the same minute
// stay stable (prevents a jarring flash on hot-reload).
function initialIndex() {
  const now = Date.now();
  const bucket = Math.floor(now / 30000); // 30-second buckets
  return bucket % MESSAGES.length;
}

function useRotatingMessage(intervalMs = 30000) {
  const [idx, setIdx] = useState(initialIndex);
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % MESSAGES.length), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return MESSAGES[idx];
}

// Compact variant — pinned to the bottom of the sidebar. Glassmorphic
// tile with a subtle plum wash. Never intrusive — reads as ambient.
export function ThriveAppreciationSidebar() {
  const message = useRotatingMessage();
  return (
    <div
      className="mx-3 mb-2 rounded-xl border border-[#BAA9CA]/30 px-3 py-3 backdrop-blur-md transition-colors"
      style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.72) 0%, rgba(241,237,245,0.62) 100%)' }}
      data-testid="thrive-appreciation-sidebar"
    >
      <div className="flex items-center gap-1.5 mb-1">
        <Heart size={11} className="text-[#685B72]" fill="currentColor" strokeWidth={0} />
        <div className="text-[9px] uppercase tracking-widest text-[#685B72]/80 font-semibold">From Thrive</div>
      </div>
      <div
        className="text-[11.5px] leading-snug text-[#685B72] italic"
        style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
      >
        {message}
      </div>
    </div>
  );
}

// Celebratory variant — larger, deliberately elegant card for the
// Dashboard footer. Multi-layered soft shadow + wider padding for a
// premium SaaS feel; typography leans on the serif family for warmth.
export function ThriveAppreciationDashboard() {
  const message = useRotatingMessage();
  return (
    <div
      className="relative overflow-hidden rounded-3xl border border-[#BAA9CA]/25 px-8 py-10 backdrop-blur-md transition-all"
      style={{
        background:
          'radial-gradient(120% 140% at 15% 0%, rgba(186,169,202,0.22) 0%, rgba(255,255,255,0.72) 45%, rgba(232,203,132,0.10) 100%)',
        boxShadow:
          '0 1px 2px rgba(74,60,90,0.04), 0 8px 20px -8px rgba(74,60,90,0.10), 0 24px 48px -16px rgba(74,60,90,0.08)',
      }}
      data-testid="thrive-appreciation-dashboard"
    >
      {/* Decorative flourish — faint plum orb bottom-right */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-16 -right-16 h-56 w-56 rounded-full opacity-40"
        style={{ background: 'radial-gradient(closest-side, rgba(186,169,202,0.35), transparent 70%)' }}
      />
      <div className="relative flex items-start gap-4">
        <div
          className="shrink-0 rounded-full p-3"
          style={{ background: 'rgba(186,169,202,0.22)' }}
        >
          <Heart size={20} className="text-[#685B72]" fill="currentColor" strokeWidth={0} />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#685B72]/80 font-semibold">
            A note from Thrive Management
          </div>
          <blockquote
            className="mt-2 text-[#2D2833] text-2xl leading-snug italic"
            style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 }}
          >
            “{message}”
          </blockquote>
          <div className="mt-3 text-[11px] uppercase tracking-widest text-[#685B72]/70 font-semibold">
            With gratitude · Thrive Speech Therapy
          </div>
        </div>
      </div>
    </div>
  );
}
